import { TableRow, TableCell, IconButton, Collapse, Box, Table, TableHead, TableBody, Button, Tooltip, Typography, Grid } from "@material-ui/core";
import React from "react";
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import ImportExportIcon from '@material-ui/icons/ImportExport';
import { LinesData } from "./DealsContext";
import SystemsContext, { SystemButton, SystemButtonStateCollection, SystemCollection, SystemsData } from "./SystemsContext";
import RemoveIcon from '@material-ui/icons/Remove';
import { Utilities } from "../general/utilities";
import { IdSet, LinesView, SystemCallbackType, TrialProducts } from "./Paperbase";
import { SystemActivationData } from "./ActivateLicense";
import { LicenseType } from "./ActivateLicenseData";
import PartialFulfillmentDialog from "./PartialFulfillmentDialog";
import { DeltaXMLColors, MyDeltaColors } from "./styling/DeltaColors";
import { LicenseSort } from "../general/sorting/LicenseSort";
import { SortOrder, SortBy, SortView, SortViewData, SortData } from "../general/sorting/LicenseSortParams";
import { DTFormatter } from "../general/datetime-formatter/DTFormatter";
import GetAppIcon from '@material-ui/icons/GetApp';
import { SaaSAuthContext } from "../authentication/SaaSAuthContext";
import ErrorDialogSaaS from "./ErrorDialogSaaS";
import TrendingFlatSharpIcon from '@material-ui/icons/TrendingFlatSharp';
import QuickCompare from "./QuickCompare";


const styles = (theme: Theme) =>
  createStyles({
    root: {
      '& > *': {
        borderBottom: 'unset',
      },
    },
    systemsRow: {
      background: DeltaXMLColors.OXFORD_BLUE,
    },
    systemRowText: {
      color: MyDeltaColors.WHITE,
    },
    systemsCreateRow: {
      background: DeltaXMLColors.CONCRETE,
    },
    expiredTableCellText: {
      color: DeltaXMLColors.VALENTINE_RED,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textAlign: 'left'
    },
    unexpiredTableCellText: {
      color: DeltaXMLColors.OXFORD_BLUE,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textAlign: 'left'
    },
    systemsTable: {
      backgroundColor: MyDeltaColors.LAVENDER_MIST
    },
    textContainer: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '5px'
    },
    cell: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    cellWithTooltip: {
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer'
    },
    sortButton: {
      color: MyDeltaColors.WHITE,
      backgroundColor: 'transparent',
      padding: '3px',
      marginRight: '5px'
    },
    activeButton: {
      color: DeltaXMLColors.OXFORD_BLUE
    },
    inactiveButton: {
      color: MyDeltaColors.INACTIVE_FOREGROUND,
      backgroundColor: DeltaXMLColors.CONCRETE,
      borderColor: MyDeltaColors.INACTIVE_BORDER,
      '&:hover': {
        backgroundColor: DeltaXMLColors.CONCRETE,
        color: MyDeltaColors.INACTIVE_FOREGROUND,
        borderColor: MyDeltaColors.INACTIVE_BORDER
      }
    },
    button: {
      margin: '2px'
    },
  });

export interface LinesContextProps extends WithStyles<typeof styles> {
  dealId: string,
  lineNumber: string,
  systemCollection: SystemCollection,
  line: LinesData,
  lineOpen: boolean,
  systemButtons: SystemButtonStateCollection;
  handleLineOpen: (dealId: string, lineId: string, view: LinesView) => void;
  callSystemAction: SystemCallbackType;
  createPartialFulfillmentSystem: (idSet: IdSet, fulfillmentSize: number) => void;
  downloadLicense: (dealId: string, lineNumber: string, systemId: string, licenseModel: string) => void;
  addUserAnnotationToSystem: (idSet: IdSet, user_annotation: string) => void;
  handleSortViewChange: (sortView: SortView, sortData: SortData) => void;
  sortViewData: SortViewData;
  linesView: LinesView,
  enableSystemActionButtons: boolean,
  dateFormat: string;
  timeFormat: string;
}

function LinesContext(props: LinesContextProps) {
  const { line, linesView, classes, systemCollection, sortViewData, handleSortViewChange, handleLineOpen, dealId, lineNumber, lineOpen, systemButtons, callSystemAction, createPartialFulfillmentSystem, downloadLicense, addUserAnnotationToSystem, enableSystemActionButtons, dateFormat, timeFormat} = props;
  const formattedSupportExpiry = DTFormatter.getFormattedDate(line.supportExpiry, dateFormat);
  const formattedLicenseExpiry = DTFormatter.getFormattedDate(line.licenseExpiry, dateFormat);
  const [openQuickCompareDialog, setQuickCompareDialog] = React.useState(false)
  let sizeColumnHeader = '';
  let sizeColumnToolTip = ''
  if (line.licenseModel === LicenseType.concurrent_operation) {
    sizeColumnHeader = 'Operations';
    sizeColumnToolTip = 'The number of operations that can run at the same time on the System';
  } else if (line.licenseModel === LicenseType.rest_capacity) {
    sizeColumnHeader = 'Capacity';
    sizeColumnToolTip = 'The number of REST operations that can run at the same time on the System';
  } else {
    sizeColumnHeader = 'Size';
    sizeColumnToolTip = 'The maximum number of runtime cores that are permissible on the System';
  }
  const saaSContext = React.useContext(SaaSAuthContext);
  const hasLicenseExpired = Utilities.hasExpired(line.licenseExpiry);
  const hasSupportExpired = Utilities.hasExpired(line.supportExpiry);
  let activeSystemCount = 0;
  let fulfillmentCount = 0;
  let systemsComponents: JSX.Element[] = [];
  let qtyData: JSX.Element = <></>;
  const notRedist = line.licenseModel !== LicenseType.redistribution;
  const [partialFulfillmentOpen, setPartialFulfillmentOpen] = React.useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = React.useState(false);
  const sortData = sortViewData[SortView.SYSTEMS];

  const interimCallSystemFunction = (idSet: IdSet, button: SystemButton, data?: SystemActivationData) => {
    const newIdSet: IdSet = [dealId, lineNumber, idSet[2]];
    callSystemAction(newIdSet, button, data);
  };

  const classForButtonState = () => {
    if (enableSystemActionButtons) {
      return classes.button + ' ' + classes.activeButton;
    } else {
      return classes.button + ' ' + classes.inactiveButton;
    }
  };

  const isSubscriptionView = linesView === LinesView.CURRENT_SUBS || linesView === LinesView.HISTORIC_SUBS;
  const isEvaluationView = linesView === LinesView.CURRENT_EVALS || linesView === LinesView.HISTORIC_EVALS;

  if (systemCollection) {
    Object.entries(systemCollection).forEach((kvp) => {
      const [name, systemValue] = kvp;
      fulfillmentCount = fulfillmentCount + systemValue.fulfillmentSize;
      if (systemValue.hostId) {
        if (line.allowedHostIds.includes("IP Address x3") || line.allowedHostIds.includes("Mac Address x3") || line.allowedHostIds.includes("Hostname x3")) {
          activeSystemCount = activeSystemCount + systemValue.fulfillmentSize;
        } else {
          activeSystemCount++;
        }
      }
    });

    const systemDataArray = Object.entries(systemCollection).map((kvp: [string, SystemsData]) => {
      return kvp[1];
    });
    const sortedSystemDataArray = systemDataArray.sort(Utilities.compareSystem);

    systemsComponents = sortedSystemDataArray
      .sort((a: any, b: any) => LicenseSort.sortLinesTable(a, b, sortData.order, sortData.sortBy))
      .map((data: SystemsData) => {

        return <SystemsContext key={data.systemId} linesView={linesView} enableSystemActionButtons={enableSystemActionButtons} dealId={dealId}
                               line={line} systemData={data} hasExpired={hasSupportExpired} systemButtons={systemButtons[data.systemId]}
                               callSystemAction={interimCallSystemFunction} downloadLicense={downloadLicense} addUserAnnotationToSystem={addUserAnnotationToSystem}
                               dateFormat={dateFormat} timeFormat={timeFormat}/>
      });

    if (isSubscriptionView) {
      qtyData = <>{line.currentQuantity}/<b>{line.quantity}</b></>;
    } else if (isEvaluationView){
      qtyData = (line.licenseModel !== LicenseType.redistribution) ? <>{line.quantity - activeSystemCount}/<b>{line.quantity}</b></> : <><b>{line.quantity}</b></>;
    } else {
      qtyData = (line.licenseModel !== LicenseType.redistribution) ? <>{line.quantity - activeSystemCount}/<b>{line.quantity}</b></> : <><b>{line.quantity}</b></>;
    }
  }

  const partialFulfillmentModel = line.allowedHostIds.includes("IP Address x3") || line.allowedHostIds.includes("Mac Address x3") || line.allowedHostIds.includes("Hostname x3");

  const handlePartialFulfillmentOpen = () => {
    // only show CreateSystem dialog when no other actions
    // like GetSystems or Activate are in progress
    if (enableSystemActionButtons) {
      setPartialFulfillmentOpen(true);
    }
  };

  const handlePartialFulfillmentClose = () => {
    setPartialFulfillmentOpen(false);
  }

  const handlePartialFulfillmentConfirm = (fulfillmentSize: number) => {
    setPartialFulfillmentOpen(false);
    createPartialFulfillmentSystem([dealId, lineNumber, ''], fulfillmentSize);
  }

  const handleDownloadTokenBtnOpen = () => {
    const lineProduct = line.product as TrialProducts
    //if button during refresh state or the has error occured
    if (saaSContext.isError[lineProduct] === true) {
      setErrorDialogOpen(true);
    } else {
      saaSContext.handleSwitchStateButton(lineProduct, enableSystemActionButtons)
      if (enableSystemActionButtons) {
        <ErrorDialogSaaS dialogOpen={errorDialogOpen} handleCloseDialog={handleErrorDialogClose} errorMessage={saaSContext.downloadTokenDone} />
      }
    }
  }
  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
  }

  const onSortChange = (sortBy: SortBy) => {
    let nextSort = sortData.order === SortOrder.DESCENDING ?
      SortOrder.ASCENDING :
      SortOrder.DESCENDING;

    handleSortViewChange(SortView.SYSTEMS, { order: nextSort, sortBy: sortBy} )
  }

  const handleQuickCompareOpen = (open: boolean) => {
    setQuickCompareDialog(open);
  }

  const handleDeltaJSONButtonOpen = () => {
    window.open(process.env.REACT_APP_DELTAJSON_APP_URL, '_blank', 'noopener,noreferrer');
  }

  let tooltipText = '';
  const qtyGreaterThanOne = line.quantity > 1;
  const remQtyGreaterThanOne = (line.quantity - activeSystemCount) > 1
  switch (line.licenseModel) {
    case LicenseType.rest_capacity:
    case LicenseType.concurrent_operation:
      tooltipText = `You can create systems that may run a total of ${line.quantity} concurrent ${qtyGreaterThanOne ? 'operations' : 'operation'}. You have ${line.quantity - fulfillmentCount} remaining ${remQtyGreaterThanOne ? 'operations' : 'operation'} to allocate to a system. You have ${fulfillmentCount - activeSystemCount} operations to activate`;
      break;
    case LicenseType.fixed_host_rest:
      tooltipText = `You have ${line.quantity} fixed-host ${qtyGreaterThanOne ? 'licenses' : 'license'}, ${line.quantity - activeSystemCount} of which ${remQtyGreaterThanOne ? 'are' : 'is'} still available for activation`;
      break;
    case LicenseType.redistribution:
      tooltipText = `You have ${line.quantity} ${qtyGreaterThanOne ? 'licenses' : 'license'} available`;
      break;
    case LicenseType.saas_starter_subscription:
    case LicenseType.saas_subscription:
      tooltipText = `Your subscription usage count is ${line.currentQuantity}. The usage limit is ${line.quantity}`;
      break;
    default:
      tooltipText = `You have ${line.quantity} ${qtyGreaterThanOne ? 'licenses' : 'license'}, ${line.quantity - activeSystemCount} of which ${remQtyGreaterThanOne ? 'are' : 'is'} still available for activation`;
      break;
  }

  const quickCompareTooltipTitle = "Try a " + line.product + " comparison";

  return (
    <React.Fragment>
      <TableRow className={classes.root}>
        <TableCell>
          <IconButton style={{ padding: '3px 0px 3px 0px' }} aria-label="expand row" size="small" onClick={() => handleLineOpen(dealId, lineNumber, linesView)}>
            {(!systemCollection) ? <RemoveIcon /> : lineOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell className={classes.textContainer} >{line.lineNumber}</TableCell>
        <TableCell className={classes.cell}>{line.product}</TableCell>
        <TableCell className={classes.cell}>{line.licenseType}</TableCell>
        <TableCell className={classes.cell}>{line.licenseModel}</TableCell>
        <TableCell className={(hasLicenseExpired) ? classes.expiredTableCellText : classes.unexpiredTableCellText}>{formattedLicenseExpiry}</TableCell>
        <TableCell className={(hasSupportExpired) ? classes.expiredTableCellText : classes.unexpiredTableCellText}>{formattedSupportExpiry}</TableCell>
        <TableCell className={classes.cell}>
          <Tooltip title={tooltipText} arrow>
            <Typography className={classes.cellWithTooltip}>{qtyData}</Typography>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow className={classes.root}>
        <TableCell style={{ paddingBottom: 5, paddingTop: 0 }} colSpan={8}>
          <Collapse in={lineOpen} timeout="auto" unmountOnExit>
            <Box margin={0}>
              <Table size="small" aria-label="purchases" className={classes.systemsTable}>
                <colgroup>
                  <col width="30%" />
                  <col width="18%" />
                  <col width="20%" />
                  {(partialFulfillmentModel) && (
                    <col width="12%" />
                  )}
                  <col width="20%" />
                </colgroup>
                {(!isSubscriptionView) &&
                  <TableHead>
                    <TableRow className={classes.systemsRow}>
                      <TableCell className={classes.systemRowText} colSpan={(notRedist) ? 1 : 3}>
                        <Tooltip title="Corresponds to a computer either running our software directly or running a license server, depending on License Type" placement="bottom-start">
                          <Grid container direction="row" onClick={() => onSortChange(SortBy.SYSTEM_ID)}>
                            <Grid item>
                              <IconButton className={classes.sortButton} size="small">
                                <ImportExportIcon fontSize="small" />
                              </IconButton>
                            </Grid>
                            <Grid item>
                              <Typography className={classes.cellWithTooltip}>System</Typography>
                            </Grid>
                          </Grid>
                        </Tooltip>
                      </TableCell>
                      {(notRedist) && (
                        <>
                          <TableCell className={classes.systemRowText}>
                            <Tooltip title="The type of information used to verify the identity of the system" placement="bottom-start">
                              <Grid container direction="row" onClick={() => onSortChange(SortBy.HOST_TYPE)}>
                                <Grid item>
                                  <IconButton className={classes.sortButton} size="small">
                                    <ImportExportIcon fontSize="small" />
                                  </IconButton>
                                </Grid>
                                <Grid item>
                                  <Typography className={classes.cellWithTooltip}>Host Type</Typography>
                                </Grid>
                              </Grid>
                            </Tooltip>
                          </TableCell>
                          <TableCell className={classes.systemRowText}>
                            <Tooltip title="The information used to verify the identity the system" placement="bottom-start">
                              <Grid container direction="row" onClick={() => onSortChange(SortBy.HOST_ID)}>
                                <Grid item>
                                  <IconButton className={classes.sortButton} size="small">
                                    <ImportExportIcon fontSize="small" />
                                  </IconButton>
                                </Grid>
                                <Grid item>
                                  <Typography className={classes.cellWithTooltip}>Host Id</Typography>
                                </Grid>
                              </Grid>
                            </Tooltip>
                          </TableCell>
                        </>
                      )}
                      {(partialFulfillmentModel) && (
                        <TableCell className={classes.systemRowText}>
                          <Tooltip title={sizeColumnToolTip} placement="bottom-start">
                            <Grid container direction="row" onClick={() => onSortChange(SortBy.PARTIAL_FULFILLMENT)}>
                              <Grid item>
                                <IconButton className={classes.sortButton} size="small">
                                  <ImportExportIcon fontSize="small" />
                                </IconButton>
                              </Grid>
                              <Grid item>
                                <Typography className={classes.cellWithTooltip}>{sizeColumnHeader}</Typography>
                              </Grid>
                            </Grid>
                          </Tooltip>
                        </TableCell>
                      )}
                      <TableCell className={classes.systemRowText}>
                        <Tooltip title="The available Actions for the System" placement="bottom-start">
                          <Typography className={classes.cellWithTooltip}>Actions</Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                }
                <TableBody>
                  {systemsComponents}
                  {partialFulfillmentModel && (
                    <TableRow className={classes.systemsCreateRow}>
                      <TableCell colSpan={2} />
                      <TableCell colSpan={2} style={{ textAlign: "right" }}>
                        <b>Remaining: {line.quantity - fulfillmentCount}</b>
                      </TableCell>
                      <TableCell>
                        {(line.quantity - fulfillmentCount > 0) && (linesView === LinesView.CURRENT || linesView === LinesView.CURRENT_EVALS) && (
                          <>
                            <Button className={classForButtonState()} variant="contained" color="primary" onClick={handlePartialFulfillmentOpen}>Create System</Button>
                            <PartialFulfillmentDialog dialogOpen={partialFulfillmentOpen} handleCloseDialog={handlePartialFulfillmentClose} partialFulfillmentConfirm={handlePartialFulfillmentConfirm} availableSize={line.quantity - fulfillmentCount} />
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                  {isSubscriptionView && (
                    <TableRow className={classes.systemsCreateRow}>
                      <TableCell style={{ textAlign: "right" }}>
                        <b>Input Size Limit: </b>{line.inputSizeLimitMb} MB
                      </TableCell>
                      <TableCell style={{ color: DeltaXMLColors.MOJO }}>
                        <b style={{ float: 'right' }}>Get the Token used to authorise API calls</b>
                      </TableCell>
                      {(linesView === LinesView.CURRENT_SUBS) && (
                        <>
                          <TableCell colSpan={2}>
                            <Grid container>
                              <Grid item xs={1} style={{ marginTop: '10px' }}>
                                <TrendingFlatSharpIcon style={{ fontSize: "54px", color: DeltaXMLColors.MOJO }} />
                              </Grid>
                              <Grid item xs={4} style={{ marginTop: '15px' }}>
                                <Tooltip title="Download the token required to use the service" arrow>
                                  <Button className={classForButtonState()} startIcon={<GetAppIcon />} variant="contained" color="primary" onClick={handleDownloadTokenBtnOpen}>Download Token</Button>
                                </Tooltip>
                                <ErrorDialogSaaS dialogOpen={errorDialogOpen} handleCloseDialog={handleErrorDialogClose} errorMessage={saaSContext.downloadTokenErrorMsg} />
                              </Grid>
                              <Grid item xs={7} style={{ marginTop: '15px' }}>
                              {line.product.includes('Content Compare') && (
                                <>
                                  <Tooltip title={quickCompareTooltipTitle} arrow>
                                    <Button className={classForButtonState()} variant="contained" color="primary" onClick={() => handleQuickCompareOpen(true)}>Quick Compare</Button>
                                  </Tooltip>
                                  <QuickCompare open={openQuickCompareDialog} handleQuickCompareDialogOpen={handleQuickCompareOpen} productId={line.product as TrialProducts} /> 
                                </>
                              )}
                              {line.product === ('DeltaJSON SaaS') && (
                                <Button className={classForButtonState()} variant="contained" color="primary" onClick={() => handleDeltaJSONButtonOpen()}>Launch DeltaJSON</Button>
                              )}
                              </Grid>
                            </Grid>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default withStyles(styles)(LinesContext);